import { Component, inject } from '@angular/core';
import { Location } from '@angular/common';
import { OrderComercioService } from 'src/app/services/order-comercio.service';
import { UsuarioService } from 'src/app/services/usuario.service';
import { AuthService } from 'src/app/services/auth.service';
import { OrdenComercio } from 'src/app/models/OrdenComercio';
import { ActivatedRoute, Router } from '@angular/router';
import { API_URI } from 'src/environments/environment';
import { CalificacionOrdenComercio } from 'src/app/models/CalificacionOrdenComercio';
import { LoadingService } from 'src/app/services/loading.service';
import { firstValueFrom } from 'rxjs';

type Opcion = {
  valor: number;
  class: string;
  active: boolean;
  clicked: boolean;
};

@Component({
    selector: 'app-review-comercio',
    templateUrl: './review-comercio.component.html',
    styleUrls: ['./review-comercio.component.css'],
    standalone: false
})
export class ReviewComercioComponent {
  private _authService = inject(AuthService);
  private _orderComercioService = inject(OrderComercioService);
  private _usuarioService = inject(UsuarioService);
  private _loadingService = inject(LoadingService);
  private router = inject(Router);
  private aRoute = inject(ActivatedRoute);
  private _location = inject(Location);

  error: string = '';
  id_orden_comercio: number;
  id_perfil_usuario: number;

  ordenComercio?: OrdenComercio;
  preview_img_avatar?: string;
  nombre?: string;

  calificacionOrdenComercio?: CalificacionOrdenComercio;

  n_experiencia: number = 0;
  n_pregunta_2: number = 0;
  x_comentario: string = '';

  experiencias: Opcion[] = [];
  preguntas_2: Opcion[] = [];

  isFinished: boolean = false;

  constructor() {
    this.id_orden_comercio = Number(this.aRoute.snapshot.paramMap.get('id_orden_comercio'));
    this.id_perfil_usuario = this._usuarioService.usuario().id_perfil!;

    this.experiencias = this.buildOpciones(0);
    this.preguntas_2 = this.buildOpciones(0);
  }

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    if (!this.id_orden_comercio) {
      this.router.navigate(['/dashboard']);
      return;
    }

    await this._loadingService.run(async () => {
      try {
        await this._authService.ensureSession();

        const [resCalOC, resOrden] = await Promise.all([
          firstValueFrom(this._orderComercioService.getCalificacionOrdenComercioXOrdenComercio(this.id_orden_comercio)),
          firstValueFrom(this._orderComercioService.getOrdenComercio(this.id_orden_comercio)),
        ]);

        // Si no existe la orden
        if (!resOrden) {
          this.error = 'La orden de comercio no existe o fue eliminada.';
          this.router.navigate(['/dashboard']);
          return;
        }

        this.ordenComercio = resOrden;
        this.setHeader();

        if (resCalOC) {
          this.calificacionOrdenComercio = resCalOC;

          this.n_experiencia = Number(resCalOC.n_experiencia) || 0;
          this.n_pregunta_2 = Number(resCalOC.n_pregunta_2) || 0;
          this.x_comentario = resCalOC.x_comentario ?? '';

          // Pintar opciones como ya calificadas
          this.experiencias = this.buildOpciones(this.n_experiencia, true);
          this.preguntas_2 = this.buildOpciones(this.n_pregunta_2, true);
        }
      } catch (e: any) {
        console.error(e);

        // Si es auth recién ahí mandamos a sign-in
        if (e?.status === 401 || e?.status === 403) {
          this.error = e?.message || 'No se pudo cargar tu cuenta.';
          this.router.navigate(['/sign-in']);
          return;
        }

        // Caso normal: error de API/red
        this.error = e?.error?.message || e?.message || 'No se pudo cargar el detalle.';
      }
    });
  }

  private setHeader(): void {
    if (!this.ordenComercio) return;

    const perfil = this._usuarioService.usuario().id_perfil;

    switch (perfil) {
      case 1: // ADMIN
      case 4: // COMERCIO -> ve cliente
        this.preview_img_avatar = this.ordenComercio.imgAvatarCliente
          ? `${API_URI}uploads/images/large-${this.ordenComercio.imgAvatarCliente}`
          : './assets/img/perfil-comercio-n.png';
        this.nombre = this.ordenComercio.nombreCliente ?? '';
        break;

      case 2: // CLIENTE -> ve comercio
      case 3: // PRESTADOR -> ve comercio
      default:
        this.preview_img_avatar = this.ordenComercio.imgAvatarComercio
          ? `${API_URI}uploads/images/large-${this.ordenComercio.imgAvatarComercio}`
          : './assets/img/perfil-comercio-n.png';
        this.nombre = this.ordenComercio.nombreComercio ?? '';
        break;
    }
  }

  private getClase(index: number): string {
    if (index < 6) return 'option-low';
    if (index < 9) return 'option-medium';
    return 'option-high';
  }

  /** Construye las 10 opciones. Si `lock=true`, deja clicked=true hasta el valor seleccionado (modo solo lectura). */
  private buildOpciones(selected: number, lock: boolean = false): Opcion[] {
    const list: Opcion[] = [];
    for (let i = 1; i <= 10; i++) {
      const isOn = selected >= i;
      list.push({
        valor: i,
        class: this.getClase(i),
        active: isOn,
        clicked: lock ? isOn : isOn,
      });
    }
    return list;
  }

  // -------- click calificar --------
  calificarExpericencia(numero: number) {
    if (this.calificacionOrdenComercio) return; // ya existe -> readOnly
    this.n_experiencia = numero;
    this.experiencias = this.buildOpciones(numero, false);
  }

  calificarPregunta2(numero: number) {
    if (this.calificacionOrdenComercio) return; // ya existe -> readOnly
    this.n_pregunta_2 = numero;
    this.preguntas_2 = this.buildOpciones(numero, false);
  }

  // -------- hover --------
  hoverCalificacion(numero: number, tipo: 'experiencias' | 'preguntas_2') {
    if (this.calificacionOrdenComercio) return; // readOnly

    const lista = (tipo === 'experiencias') ? this.experiencias : this.preguntas_2;

    lista.forEach((op) => {
      if (numero > 0) {
        op.active = op.valor <= numero;
      } else {
        // vuelve al estado "seleccionado" real
        if (tipo === 'experiencias') op.active = op.valor <= this.n_experiencia;
        else op.active = op.valor <= this.n_pregunta_2;
      }
    });
  }

  // -------- enviar --------
  async finalizaReviewComercio(): Promise<void> {
    this.error = '';

    if (this.calificacionOrdenComercio) return; // ya calificado
    if (!this.ordenComercio) {
      this.error = 'No se pudo cargar el pedido.';
      return;
    }

    if (this.n_experiencia === 0 || this.n_pregunta_2 === 0) {
      this.error = 'Debe calificar ambas opciones';
      return;
    }

    const payload: CalificacionOrdenComercio = {
      id_usuario: this._usuarioService.usuario().id!,
      id_comercio: this.ordenComercio.id_comercio!,
      id_orden_comercio: this.ordenComercio.id!,
      n_experiencia: this.n_experiencia,
      n_pregunta_2: this.n_pregunta_2,
      x_comentario: (this.x_comentario ?? '').trim(),
    };

    await this._loadingService.run(async () => {
      try {
        await firstValueFrom(this._orderComercioService.createCalificacionOrdenComercio(payload));
        // Estado CALIFICADO = 9
        await firstValueFrom(this._orderComercioService.updateEstadoOrdenComercio(this.ordenComercio!.id!, 9));
        this.isFinished = true;
      } catch (e) {
        console.error(e);
        this.error = 'No se pudo enviar la calificación. Intente nuevamente.';
      }
    });
  }

  back(){
    this._location.back();
  }
}
